import { NotificationType } from '@common/notificationTypes';
import { NewestSnapshots, notificationsToApply } from '@common/notificationDelivery';
import { NotificationCursor } from '@common/notificationCursor';
import { collection, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
// ===============================
// Firebase Notification Service Implementation
// All Firestore details are encapsulated here — App never sees them.
// ===============================
export class FirebaseNotificationService {
    constructor(db, logger) {
        this.db = db;
        this.logger = logger;
        this.unsubscribe = null;
        this.visibilityTimer = null;
        this.userId = null;
        this.listeners = [];
        this.callbacks = null;
        this.beforeUnloadHandler = null;
        this.heartbeatTimer = null;
        this.lastHeartbeatAt = 0;
        this.heartbeatInFlight = false;
        /** When the data the app shows was read (server time); null until the sign-in data is in. */
        this.dataReadAtUtc = null;
        /**
         * Where this tab reads notifications from (they're shared with the user's other tabs, so none are deleted);
         * null until the sign-in data is in, which the listener starts from.
         */
        this.cursor = null;
        /** When this tab was hidden; null while it's shown. */
        this.hiddenAtMs = null;
        /** The newest snapshot applied of each thing: an older one arriving late is skipped. */
        this.newest = new NewestSnapshots();
        /** Bumped by start() and stop(): what a previous session's request or timer finishes doesn't touch this one. */
        this.session = 0;
        this.resyncing = false;
        /** A reload asked for while one ran: it runs once that one ends (that one may have read too early). */
        this.resyncQueued = null;
        /** A reload is owed (one failed): it's tried again when the tab is next shown or sends its heartbeat. */
        this.resyncOwed = false;
        /** A reload waiting for the heartbeat in flight: the subscription is renewed before the data is read again. */
        this.reloadAfterHeartbeat = null;
        // Clean disconnect after 5 minutes of inactivity
        this.DISCONNECT_TIMEOUT_MS = 5 * 60 * 1000;
        // Subscriptions expire a day after their last sign-in or heartbeat
        this.HEARTBEAT_INTERVAL_MS = 60 * 60 * 1000;
        // Notifications read at a time, oldest first; a full read moves on to the next
        this.WINDOW_SIZE = 100;
        // The largest read, counting those read again to catch any written late; a burst past it reloads the data
        this.MAX_READ_SIZE = 500;
        // A notification can be written this long after a newer one (a batch writes what it raised together)
        this.LATE_WINDOW_MS = 2 * 60 * 1000;
        // Notifications are deleted a day after they're written: a tab away (or asleep) longer reloads its data
        this.RELOAD_AFTER_AWAY_MS = 23 * 60 * 60 * 1000;
        this.setupVisibilityListener();
    }
    onNotification(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
    start(userId, callbacks) {
        // A previous session may not have signed out (another user signed in): its listener, timer and page-close
        // handler go. It isn't unsubscribed: the API now acts as the new user, and the old entry simply expires.
        this.teardown();
        this.session++;
        this.userId = userId;
        this.callbacks = callbacks;
        this.newest = new NewestSnapshots();
        // Listening starts once the sign-in data is in (markDataLoadedAt).
        // Hidden tabs keep it going too: a tab that comes back catches up on what it missed.
        this.lastHeartbeatAt = Date.now();
        this.heartbeatTimer = setInterval(() => this.sendHeartbeat(null), this.HEARTBEAT_INTERVAL_MS);
        // Unsubscribe on page close (keepalive fetch survives navigation)
        if (typeof window !== 'undefined') {
            this.beforeUnloadHandler = () => {
                this.callbacks?.unsubscribeSync();
            };
            window.addEventListener('beforeunload', this.beforeUnloadHandler);
        }
    }
    markDataLoadedAt(serverTimeUtc) {
        // Read from the data's own (server) time: notifications stay until their TTL, so everything written since is
        // still there, and this tab's clock doesn't come into it.
        this.dataReadAtUtc = serverTimeUtc;
        this.cursor = new NotificationCursor(serverTimeUtc, this.LATE_WINDOW_MS, this.WINDOW_SIZE, this.MAX_READ_SIZE);
        this.newest = new NewestSnapshots();
        this.resyncOwed = false;
        this.unsubscribe?.();
        this.unsubscribe = null;
        if (this.hiddenAtMs === null || Date.now() - this.hiddenAtMs < this.DISCONNECT_TIMEOUT_MS)
            this.subscribe();
    }
    reload(reason) {
        this.resync(reason);
    }
    stop() {
        const callbacks = this.callbacks;
        this.teardown();
        this.session++;
        // Unregister from backend
        callbacks?.unsubscribe().catch(() => { });
        this.callbacks = null;
        this.userId = null;
    }
    /** Stop listening, and drop the session's timers, page-close handler and reload state. */
    teardown() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.dataReadAtUtc = null;
        this.cursor = null;
        this.resyncing = false;
        this.resyncQueued = null;
        this.resyncOwed = false;
        this.reloadAfterHeartbeat = null;
        this.heartbeatInFlight = false;
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        if (this.beforeUnloadHandler && typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
            this.beforeUnloadHandler = null;
        }
        if (this.visibilityTimer) {
            clearTimeout(this.visibilityTimer);
            this.visibilityTimer = null;
        }
    }
    subscribe() {
        const cursor = this.cursor;
        if (this.unsubscribe || !this.userId || cursor === null)
            return;
        const readSize = cursor.limit;
        try {
            const q = query(collection(this.db, 'users', this.userId, 'notifications'), where('createdAt', '>=', cursor.start), orderBy('createdAt', 'asc'), limit(readSize));
            // Oldest first, including a backlog written while this tab wasn't listening (hidden): applied in order.
            let readingOn = false;
            this.unsubscribe = onSnapshot(q, (snapshot) => {
                // A snapshot of a listener since replaced (a reload started a new one) is left to that one.
                if (this.cursor !== cursor || readingOn)
                    return;
                const arrived = [];
                for (const change of snapshot.docChanges()) {
                    if (change.type !== 'added')
                        continue;
                    const data = change.doc.data();
                    const createdAt = typeof data.createdAt === 'number' ? data.createdAt : 0;
                    if (!cursor.take(change.doc.id, createdAt))
                        continue;
                    const notification = this.parseNotification(change.doc.id, data);
                    if (notification)
                        arrived.push(notification);
                }
                this.apply(arrived);
                // A full read brings in nothing newer: read on from where it ends (once per read).
                if (snapshot.size >= readSize) {
                    readingOn = true;
                    if (!cursor.afterFullRead())
                        this.resync('more live updates at once than a read holds');
                    setTimeout(() => this.restartListener(), 0);
                }
            }, (error) => {
                this.logger.warn('Notification listener error:', error);
                // It has stopped: coming back to the tab starts another, and what it missed is read again.
                if (this.cursor === cursor) {
                    this.unsubscribe = null;
                    this.resyncOwed = true;
                }
            });
        }
        catch (error) {
            this.logger.error('Failed to subscribe to notifications', error);
        }
    }
    /**
     * Validate and parse a Firestore document into a typed Notification.
     * Returns null if the document is malformed — caller should discard it.
     */
    parseNotification(docId, data) {
        const type = data.type;
        const payload = data.payload;
        if (!type || !payload || typeof payload !== 'object' || typeof data.createdAt !== 'number') {
            this.logger.error(`CRITICAL: Malformed notification ${docId} — missing type, payload, or createdAt. Backend contract violated.`, { type: data.type, hasPayload: !!data.payload, createdAt: data.createdAt });
            return null;
        }
        const base = {
            id: docId,
            read: data.read === true,
            createdAt: data.createdAt,
        };
        switch (type) {
            case NotificationType.CHAT_MESSAGE:
                if (!payload.chatId || !payload.message) {
                    this.logger.error(`CRITICAL: Invalid CHAT_MESSAGE payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CHAT_MESSAGE, payload: { chatId: payload.chatId, message: payload.message } };
            case NotificationType.CANDIDATE_CREATED:
                if (!payload.candidateId || !payload.candidate) {
                    this.logger.error(`CRITICAL: Invalid CANDIDATE_CREATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CANDIDATE_CREATED, payload: {
                        candidateId: payload.candidateId,
                        candidate: payload.candidate,
                        jobId: payload.jobId,
                    } };
            case NotificationType.CANDIDATE_UPDATED:
                if (!payload.candidateId) {
                    this.logger.error(`CRITICAL: Invalid CANDIDATE_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CANDIDATE_UPDATED, payload: {
                        candidateId: payload.candidateId,
                        candidate: payload.candidate,
                        jobId: payload.jobId,
                        application: payload.application,
                    } };
            case NotificationType.CANDIDATE_PROCESSING_ERROR:
                if (!payload.candidateId || !payload.error) {
                    this.logger.error(`CRITICAL: Invalid CANDIDATE_PROCESSING_ERROR payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CANDIDATE_PROCESSING_ERROR, payload: {
                        candidateId: payload.candidateId,
                        jobId: payload.jobId,
                        error: payload.error,
                        canRetryAtUtc: typeof payload.canRetryAtUtc === 'number' ? payload.canRetryAtUtc : undefined,
                        errorType: payload.errorType,
                    } };
            case NotificationType.INTERVIEW_SCHEDULED:
                if (!payload.calendarEvent || !payload.candidateId || !payload.jobId) {
                    this.logger.error(`CRITICAL: Invalid INTERVIEW_SCHEDULED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.INTERVIEW_SCHEDULED, payload: {
                        calendarEvent: payload.calendarEvent,
                        candidateId: payload.candidateId,
                        jobId: payload.jobId,
                    } };
            case NotificationType.SCHEDULED_MESSAGE_UPDATED:
                if (!payload.message?.id) {
                    this.logger.error(`CRITICAL: Invalid SCHEDULED_MESSAGE_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.SCHEDULED_MESSAGE_UPDATED, payload: {
                        message: payload.message,
                    } };
            case NotificationType.SCHEDULED_MESSAGES_UPDATED:
                if (!Array.isArray(payload.messages)) {
                    this.logger.error(`CRITICAL: Invalid SCHEDULED_MESSAGES_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.SCHEDULED_MESSAGES_UPDATED, payload: {
                        messages: payload.messages,
                    } };
            case NotificationType.JOB_IMPORT_COMPLETED:
                return { ...base, type: NotificationType.JOB_IMPORT_COMPLETED, payload: {
                        url: payload.url ?? '',
                        success: payload.success === true,
                        processingTime: typeof payload.processingTime === 'number' ? payload.processingTime : 0,
                    } };
            case NotificationType.JOB_CREATED:
                if (!payload.job) {
                    this.logger.error(`CRITICAL: Invalid JOB_CREATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.JOB_CREATED, payload: { job: payload.job } };
            case NotificationType.JOB_UPDATED:
                if (!payload.job) {
                    this.logger.error(`CRITICAL: Invalid JOB_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.JOB_UPDATED, payload: { job: payload.job } };
            case NotificationType.JOB_DELETED:
                if (!payload.jobId) {
                    this.logger.error(`CRITICAL: Invalid JOB_DELETED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.JOB_DELETED, payload: { jobId: payload.jobId } };
            case NotificationType.JOB_OPTIMIZATION_COMPLETED:
                if (!payload.jobId) {
                    this.logger.error(`CRITICAL: Invalid JOB_OPTIMIZATION_COMPLETED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.JOB_OPTIMIZATION_COMPLETED, payload: { jobId: payload.jobId } };
            case NotificationType.CANDIDATE_BATCH_COMPLETED:
                return { ...base, type: NotificationType.CANDIDATE_BATCH_COMPLETED, payload: {
                        batchId: payload.batchId ?? '',
                        total: typeof payload.total === 'number' ? payload.total : 0,
                        succeeded: typeof payload.succeeded === 'number' ? payload.succeeded : 0,
                        failed: typeof payload.failed === 'number' ? payload.failed : 0,
                    } };
            case NotificationType.CANDIDATE_DELETED:
                if (!Array.isArray(payload.candidateIds)) {
                    this.logger.error(`CRITICAL: Invalid CANDIDATE_DELETED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CANDIDATE_DELETED, payload: { candidateIds: payload.candidateIds } };
            case NotificationType.CANDIDATES_UPDATED:
                if (!Array.isArray(payload.candidates)) {
                    this.logger.error(`CRITICAL: Invalid CANDIDATES_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.CANDIDATES_UPDATED, payload: { candidates: payload.candidates } };
            case NotificationType.SCHEDULED_MESSAGE_DELETED:
                if (!payload.id || !payload.candidateId || !payload.jobId) {
                    this.logger.error(`CRITICAL: Invalid SCHEDULED_MESSAGE_DELETED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.SCHEDULED_MESSAGE_DELETED, payload: {
                        id: payload.id,
                        candidateId: payload.candidateId,
                        jobId: payload.jobId,
                    } };
            case NotificationType.USER_UPDATED:
                if (!payload.userId || !payload.profile) {
                    this.logger.error(`CRITICAL: Invalid USER_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.USER_UPDATED, payload: {
                        userId: payload.userId,
                        profile: payload.profile,
                    } };
            case NotificationType.SYSTEM_ALERT:
                return { ...base, type: NotificationType.SYSTEM_ALERT, payload: {
                        title: payload.title ?? '',
                        message: payload.message ?? '',
                        severity: ['info', 'warning', 'error'].includes(payload.severity) ? payload.severity : 'info',
                    } };
            case NotificationType.SCORING_RETRY:
                if (!payload.candidateId || !payload.jobId) {
                    this.logger.error(`CRITICAL: Invalid SCORING_RETRY payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.SCORING_RETRY, payload: {
                        candidateId: payload.candidateId,
                        jobId: payload.jobId,
                        attempt: typeof payload.attempt === 'number' ? payload.attempt : 1,
                        maxAttempts: typeof payload.maxAttempts === 'number' ? payload.maxAttempts : 3,
                        delaySec: typeof payload.delaySec === 'number' ? payload.delaySec : 0,
                        errorType: payload.errorType ?? 'server-error',
                    } };
            case NotificationType.ORG_UPDATED:
                if (!payload.orgId || !payload.subscription || !payload.usage) {
                    this.logger.error(`CRITICAL: Invalid ORG_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.ORG_UPDATED, payload: {
                        orgId: payload.orgId,
                        subscription: payload.subscription,
                        usage: payload.usage,
                    } };
            case NotificationType.COMM_METRICS_UPDATED:
                if (typeof payload.orgId !== 'string' || !Array.isArray(payload.days) || !Array.isArray(payload.jobMonths)) {
                    this.logger.error(`CRITICAL: Invalid COMM_METRICS_UPDATED payload in ${docId}`, payload);
                    return null;
                }
                return { ...base, type: NotificationType.COMM_METRICS_UPDATED, payload: {
                        orgId: payload.orgId,
                        days: payload.days,
                        jobMonths: payload.jobMonths,
                    } };
            default:
                this.logger.error(`CRITICAL: Unknown notification type "${type}" in ${docId} — backend may have added a new type not handled by this client`);
                return null;
        }
    }
    /** Apply them oldest first; they stay for the user's other tabs (a TTL removes them). */
    apply(arrived) {
        if (this.dataReadAtUtc === null || arrived.length === 0)
            return;
        const toApply = notificationsToApply(arrived, this.dataReadAtUtc)
            .filter((notification) => this.newest.accept(notification));
        if (toApply.length < arrived.length) {
            this.logger.info(`Dropping ${arrived.length - toApply.length} notification(s) older than what's shown`);
        }
        toApply.forEach((notification) => this.notifyListeners(notification));
    }
    restartListener() {
        if (!this.unsubscribe)
            return;
        this.unsubscribe();
        this.unsubscribe = null;
        this.subscribe();
    }
    /**
     * Tell the server this tab is still open. A reload it calls for (`reloadReason`, the tab having been dormant, or
     * one owed) waits for it: data read before the subscription is renewed could miss what changes in between.
     */
    sendHeartbeat(reloadReason) {
        const callbacks = this.callbacks;
        if (!callbacks)
            return;
        // Timers stall while the machine sleeps: a tab whose last heartbeat is this old was dormant at least that
        // long, and what it missed may be gone (whether or not another tab kept the subscription alive).
        const dormant = Date.now() - this.lastHeartbeatAt >= this.RELOAD_AFTER_AWAY_MS;
        this.reloadAfterHeartbeat ?? (this.reloadAfterHeartbeat = reloadReason ??
            (dormant ? 'the tab was dormant' : this.resyncOwed ? 'an earlier reload failed' : null));
        if (this.heartbeatInFlight)
            return;
        this.heartbeatInFlight = true;
        const session = this.session;
        callbacks.heartbeat()
            .then((lapsed) => {
            if (this.session !== session)
                return;
            this.lastHeartbeatAt = Date.now();
            const reason = lapsed ? 'the notification subscription had lapsed' : this.reloadAfterHeartbeat;
            this.reloadAfterHeartbeat = null;
            if (reason !== null)
                this.resync(reason);
        })
            .catch((error) => {
            if (this.session !== session)
                return;
            // The subscription may not be renewed: the reload waits for a heartbeat that gets through.
            if (this.reloadAfterHeartbeat !== null)
                this.resyncOwed = true;
            this.reloadAfterHeartbeat = null;
            this.logger.warn('Notification heartbeat failed:', error);
        })
            .finally(() => {
            if (this.session === session)
                this.heartbeatInFlight = false;
        });
    }
    /**
     * Live updates may have been missed: have the app read its data again (its `markDataLoadedAt` then starts a
     * new listener from the new data's time). Until then the current listener carries on; if the reload fails it
     * just carries on, and the reload is tried again later. One asked for while another runs follows it.
     */
    resync(reason) {
        const callbacks = this.callbacks;
        if (!callbacks || !this.userId)
            return;
        if (this.resyncing) {
            this.resyncQueued ?? (this.resyncQueued = reason);
            return;
        }
        this.resyncing = true;
        this.resyncOwed = true;
        const session = this.session;
        this.logger.info(`Reloading data: ${reason}`);
        callbacks.resync()
            .catch((error) => this.logger.warn('Reloading data failed; trying again later', error))
            .finally(() => {
            if (this.session !== session)
                return;
            this.resyncing = false;
            const queued = this.resyncQueued;
            this.resyncQueued = null;
            if (queued !== null)
                this.resync(queued);
        });
    }
    notifyListeners(notification) {
        this.listeners.forEach(listener => {
            try {
                listener(notification);
            }
            catch (e) {
                this.logger.error('Error in notification listener:', e);
            }
        });
    }
    setupVisibilityListener() {
        if (typeof document === 'undefined')
            return;
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.hiddenAtMs = Date.now();
                this.visibilityTimer = setTimeout(() => {
                    if (this.unsubscribe) {
                        this.unsubscribe();
                        this.unsubscribe = null;
                        this.logger.info('Notification listener paused due to inactivity');
                    }
                }, this.DISCONNECT_TIMEOUT_MS);
                return;
            }
            if (this.visibilityTimer) {
                clearTimeout(this.visibilityTimer);
                this.visibilityTimer = null;
            }
            // Measured from when the tab was hidden: the pause timer itself can fire late (after sleep).
            const awayMs = this.hiddenAtMs === null ? 0 : Date.now() - this.hiddenAtMs;
            this.hiddenAtMs = null;
            if (!this.userId)
                return;
            if (!this.unsubscribe && this.cursor !== null) {
                this.logger.info('Notification listener resuming...');
                this.cursor.afterPause();
                this.subscribe();
            }
            // Hidden tabs' timers can stall: refresh the subscription if it's been a while, before any reload.
            if (awayMs >= this.RELOAD_AFTER_AWAY_MS) {
                this.sendHeartbeat('away longer than live updates are kept');
            }
            else if (this.resyncOwed || Date.now() - this.lastHeartbeatAt >= this.HEARTBEAT_INTERVAL_MS) {
                this.sendHeartbeat(null);
            }
        });
    }
}
